import axios from "axios";
import { encryptForServer, decryptFromServer, parseJWTPayload, decryptPermBundle } from './permDecrypt';

const { REACT_APP_NODE_SERVER_BASE_URL } = process.env;

const unauthorizedStatusCode = [400, 401, 403];

// ---------------------------------------------------------------------------
// Integration transport crypto helpers (internal to this module)
// ---------------------------------------------------------------------------

/**
 * Encrypts a request body using the caller's JWT token.
 * Returns { __enc: "<base64>" } for POST/PUT/PATCH bodies.
 * @param {*}      data  - Payload to encrypt
 * @param {string} token - Bearer JWT from userData.token
 */
const encryptPayload = (data, token) => encryptForServer(data, token);

/**
 * Decrypts an integration API response.
 * The server wraps data as { __enc: "<base64>" }; this unwraps it.
 * Falls back to the raw data if it was not encrypted.
 * @param {*}      responseData - result.data from the axios response
 * @param {string} token        - Bearer JWT from userData.token
 */
const decryptResponse = (responseData, token) => {
    if (responseData && responseData.__enc) {
        return decryptFromServer(responseData.__enc, token);
    }
    return responseData; // non-encrypted fallback
};



const getHeaders = (token = null, organizationId = null, method = 'get', contentType = 'application/json', eventData = null) => {
    const headers = {};

    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (['post', 'put', 'patch', 'delete'].includes(method.toLowerCase()) && contentType) {
        headers['Content-Type'] = contentType;
    }
    if (organizationId) headers['organization'] = organizationId;
    if (eventData) headers['x-event-data'] = JSON.stringify(eventData);

    return headers;
};

export const getAppData = async (token, organizationId) => {
    try {
        if (!token) return;
        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/app-data`,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

export const saveContactUSForm = async (token, data) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/user/contact-us`,
            headers: getHeaders(token, null, "post"),
            data: data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('Contact us form submission Error: ', error);
        throw error;
    }
}

export const getUserProfile = async (token, userId, eventData = null) => {
    try {
        if (!token) return;

        const headers = getHeaders(token);
        if (eventData) {
            headers['x-event-data'] = JSON.stringify(eventData);
        }

        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/user/${userId}`,
            headers: headers
        };

        const result = await axios.request(config)

        let responseBody = result.data;
        if (responseBody && responseBody.data && Array.isArray(responseBody.data.organizations)) {
            const profileData = responseBody.data;
            const blockedOrgIds = profileData?.app_metadata?.blocked_orgs || [];
            const { sub: jwtSub, iat } = parseJWTPayload(token);

            profileData.organizations = profileData.organizations.map((org) => {
                if (blockedOrgIds.includes(org.org_token)) {
                    const { level, permissions, enc_perms, enc_role, ...rest } = org;
                    return { ...rest, isBlocked: true };
                }

                let decryptedOrg = { ...org };
                if (org.enc_perms) {
                    try {
                        const bundle = decryptPermBundle(org.enc_perms, jwtSub, iat);
                        const { enc_perms, ...rest } = decryptedOrg;
                        decryptedOrg = {
                            ...rest,
                            permissions: bundle.permissions,
                            level: {
                                ...org.level,
                                route_permission: bundle.route_permission,
                                feature_permission: bundle.feature_permission,
                                feature_restriction: bundle.feature_restriction,
                            },
                        };
                    } catch (e) {
                        console.error('Permission decryption failed for org:', org.name, e);
                    }
                }

                if (org.enc_role) {
                    try {
                        const role = decryptPermBundle(org.enc_role, jwtSub, iat);
                        const { enc_role, ...rest } = decryptedOrg;
                        decryptedOrg = {
                            ...rest,
                            role
                        };
                    } catch (e) {
                        console.error('Role decryption failed for org:', org.name, e);
                    }
                }

                return decryptedOrg;
            });
        }

        return responseBody;
    } catch (error) {
        console.log('error', error)
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const updateUserProfile = async (token, userId, data, organizationId = null) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/user/update/${userId}`,
            headers: getHeaders(token, organizationId, "post"),
            data: data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('updateUserProfile error', error);
        throw error;
    }
}

export const updateOrganizationRole = async (token, data) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/organization/update-member-role`,
            headers: getHeaders(token, data.organizationId, "post"),
            data: data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('updateUserProfile error', error);
        throw error;
    }
}

export const updateOrganizations = async (token, organization_id, data) => {
    try {
        if (!token) return;
        const config = {
            method: 'patch',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/organization/${organization_id}`,
            headers: getHeaders(token, organization_id, "patch"),
            data: data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('updateUserProfile error', error);
        throw error;
    }
}

export const getAllRoles = async (token) => {
    try {
        if (!token) return;
        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/organization/roles`,
            headers: getHeaders(token)
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('getUserProfile error', error)
        throw error;
    }
}

export const sendResetPasswordEmail = async (email, organizationId = null, eventData = null) => {
    try {
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/user/forgot-password`,
            headers: getHeaders(null, organizationId, "post", "application/json", eventData),
            data: { email }
        };
        const result = await axios.request(config)
        return result.data;

    } catch (error) {
        console.log('updateUserProfile error', error);
        throw error;
    }
}

export const ResendVerificationEmail = async (user_id) => {
    try {
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/user/resend-verification-email`,
            headers: getHeaders(null, null, "post"),
            data: { user_id }
        };
        const result = await axios.request(config)
        return result.data;

    } catch (error) {
        console.log('ResendVerificationEmail error', error);
        throw error;
    }
}

export const deactivateUser = async (token, userId, organizationId = null, isSelf = false) => {
    try {
        if (!token) return;
        const url = (organizationId && !isSelf)
            ? `${REACT_APP_NODE_SERVER_BASE_URL}/user/org/deactivate`
            : `${REACT_APP_NODE_SERVER_BASE_URL}/user/deactivate/${userId}`;
        const config = {
            method: 'post',
            url,
            headers: getHeaders(token, organizationId, "post"),
            data: (organizationId && !isSelf) ? { userId, organizationId } : {},
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('deactivateUser error', error);
        throw error;
    }
}

// ── Platform-admin API helpers ──────────────────────────────────────────────
// These target /user/admin/* routes which are gated by validateAdminAccess on
// the server. Used exclusively by AdminUserManagement components.

export const getAdminUserProfile = async (token, userId, organizationId) => {
    try {
        if (!token) return;
        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/user/admin/${userId}`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('getAdminUserProfile error', error);
        throw error;
    }
}

export const adminUpdateUserProfile = async (token, userId, data, organizationId) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/user/admin/update/${userId}`,
            headers: getHeaders(token, organizationId, "post"),
            data: data
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('adminUpdateUserProfile error', error);
        throw error;
    }
}

export const adminDeactivateUser = async (token, userId, organizationId) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/user/admin/deactivate`,
            headers: getHeaders(token, organizationId, "post"),
            data: { userId }
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('adminDeactivateUser error', error);
        throw error;
    }
}

export const inviteUser = async (token, data, eventData = null) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/user/invite`,
            headers: getHeaders(token, data.organizationId, "post", 'application/json', eventData),
            data: data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('deactivateUser error', error);
        throw error;
    }
}


export const addNewAdobeIoProduct = async (token, { organizationId, data }, eventData = null) => {
    try {
        if (!token) return;
        let config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/adobe-io-integration`,
            headers: getHeaders(token, organizationId, "post", 'application/json', eventData),
            data: encryptPayload(data, token)
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('addNewAdobeIoProduct error', error);
        throw error.response.data;
    }
}

export const getNewWorkspaceData = async (token, organizationId, tenant) => {
    try {
        if (!token) return;

        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/workspaces`);
        url.searchParams.append('tenant', tenant);
        let config = {
            method: 'get',
            url,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('addNewAdobeIoProduct error', error);
        throw error.response.data;
    }
}

export const addNewWorkspace = async (token, organizationId, data) => {
    try {
        if (!token) return;
        let config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/workspaces`,
            headers: getHeaders(token, organizationId, "post"),
            data: data
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('addNewAdobeIoProduct error', error);
        throw error.response.data;
    }
}

export const updateWorkspace = async (token, organizationId, id, data) => {
    try {
        if (!token) return;
        let config = {
            method: 'put',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/workspaces/${id}`,
            headers: getHeaders(token, organizationId, "put"),
            data: data
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('addNewAdobeIoProduct error', error);
        throw error.response.data;
    }
}

export const deleteWorkspace = async (token, organizationId, id) => {
    try {
        if (!token) return;
        let config = {
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/workspaces/${id}`,
            headers: {
                'Authorization': `Bearer ${token}`,
                'organization': organizationId,
                'Content-Type': 'application/json'
            },
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('addNewAdobeIoProduct error', error);
        throw error.response.data;
    }
}

export const getAllAdobeIoProduct = async (token, organizationId) => {
    try {
        if (!token) return;
        let config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/adobe-io-integration`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const getSpecificAdobeIoProduct = async (token, organizationId, productId) => {
    try {
        if (!token) return;
        let config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/adobe-io-integration/${productId}`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const updateSpecificAdobeIoProduct = async (token, organizationId, { productId, bodyData }) => {
    try {
        if (!token) return;
        let config = {
            method: 'put',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/adobe-io-integration/${productId}`,
            headers: getHeaders(token, organizationId, "put"),
            data: encryptPayload(bodyData, token)
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

export const deleteSpecificAdobeIoProduct = async (token, organizationId, productId, checkDependencies = false) => {
    try {
        if (!token) return;
        let config = {
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/adobe-io-integration/${productId}${checkDependencies ? "?checkDependencies=true" : ""}`,
            headers: getHeaders(token, organizationId, "delete"),
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

export const getIntegrationData = async (token, organizationId, product) => {
    try {
        if (!token) return;
        let config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/adobe-io-integration/?product=${product}`,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const getAllActivityData = async (token, organizationId, activityName, type, campaign_id, workspace) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/details`);
        url.searchParams.append('tenant', activityName);
        url.searchParams.append('type', type);
        url.searchParams.append('campaign_id', campaign_id);
        if (workspace) {
            url.searchParams.append('workspace', workspace);
        }
        let config = {
            url,
            method: 'get',
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

export const getAdobeAnalyticsUsageData = async (token, organizationId, tenant, startDate, endDate, metricsConfig = [], detail = false, adobe_io_id, dashboard_id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/adobe-analytics-usage`);
        url.searchParams.append('tenant', tenant);
        url.searchParams.append('start_date', startDate);
        url.searchParams.append('end_date', endDate);
        url.searchParams.append('dashboard_id', dashboard_id);
        if (metricsConfig.length) {
            url.searchParams.append('metrics_config', JSON.stringify(metricsConfig));
        }
        if (detail) {
            url.searchParams.append('detail', 'true');
        }
        if (adobe_io_id) {
            url.searchParams.append('id', adobe_io_id);
        }
        let config = {
            url,
            method: 'get',
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data;
    }
}

export const getAdobeAnalyticsScores = async (token, organizationId, dashboardId) => {
    try {
        if (!token) return;
        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/functions/adobe-analytics-scores?dashboard_id=${dashboardId}`,
            headers: getHeaders(token, organizationId)
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data;
    }
}

export const updateAdobeAnalyticsScores = async (token, organizationId, scores, dashboardId) => {
    try {
        if (!token) return;
        const config = {
            method: 'put',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/functions/adobe-analytics-scores?dashboard_id=${dashboardId}`,
            headers: getHeaders(token, organizationId, 'put'),
            data: scores
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data;
    }
}

export const getAdobeAnalyticsDashboardCards = async (token, organizationId, dashboardId) => {
    try {
        if (!token) return;
        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/functions/adobe-analytics-cards?dashboard_id=${dashboardId}`,
            headers: getHeaders(token, organizationId)
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data;
    }
}

export const saveAdobeAnalyticsDashboardCards = async (token, organizationId, cards, dashboardId) => {
    try {
        if (!token) return;
        const config = {
            method: 'put',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/functions/adobe-analytics-cards?dashboard_id=${dashboardId}`,
            headers: getHeaders(token, organizationId, 'put'),
            data: cards
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data;
    }
}

export const getAdobeTargetUsage = async (token, organizationId, tenant_name, start_date, end_date, cards, is_global, adobe_io_id, dashboard_id) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/functions/adobe-target-usage`,
            headers: getHeaders(token, organizationId, 'post'),
            data: {
                start_date,
                end_date,
                cards,
                is_global,
                adobe_io_id
            },
            params: {
                tenant: tenant_name,
                dashboard_id
            }
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data;
    }
}

export const getAdobeUsersMetadataKeys = async (token, organizationId) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/adobe-users/metadata-keys`);
        const config = {
            method: 'get',
            url: url.toString(),
            headers: getHeaders(token, organizationId)
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            sessionStorage.clear();
            localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data;
    }
}

export const getAdobeUsers = async (token, organizationId, adobe_io_id = null) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/adobe-users`);
        if (adobe_io_id) {
            url.searchParams.append('adobe_io_id', adobe_io_id);
        }
        const config = {
            method: 'get',
            url: url.toString(),
            headers: getHeaders(token, organizationId)
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data;
    }
}

export const deleteAdobeUsers = async (token, organizationId, ids, x_API_Key) => {
    try {
        if (!token) return;
        const config = {
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/functions/adobe-users`,
            headers: getHeaders(token, organizationId, "delete"),
            data: { ids, x_API_Key }
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data;
    }
}

export const uploadMetadataFromCSV = async (token, organizationId, rows) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/functions/adobe-users/upload-metadata`,
            headers: getHeaders(token, organizationId, "post"),
            data: { rows }
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data;
    }
}

export const updateAdobeUsersMetadata = async (token, organizationId, updates) => {
    try {
        if (!token) return;
        const config = {
            method: 'put',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/functions/adobe-users/metadata`,
            headers: getHeaders(token, organizationId, "put"),
            data: { updates }
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data;
    }
}

export const deleteAdobeUsersMetadataKey = async (token, organizationId, key) => {
    try {
        if (!token) return;
        const config = {
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/functions/adobe-users/metadata-key`,
            headers: getHeaders(token, organizationId, "delete"),
            data: { key }
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data;
    }
}

export const getManageRecommendations = async ({ token, organizationId, tenant, environmentId, start, limit, search }) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/manage-recommendations`);
        url.searchParams.append('tenant', tenant);
        url.searchParams.append('environment_id', environmentId);
        url.searchParams.append('limit', limit);
        url.searchParams.append('start', start);
        if (search) {
            url.searchParams.append('search', search);
        }

        const config = {
            url,
            method: 'get',
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

export const getRecommendationById = async ({ token, organizationId, tenant, environmentId, id }) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/manage-recommendation/${id}`);
        url.searchParams.append('tenant', tenant);

        const config = {
            url,
            method: 'get',
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

export const deleteRecommendationByIds = async ({ token, organizationId, tenant, environmentId, ids }) => {
    try {
        if (!token) return;

        const queryParameters = {
            ids,
            tenant: tenant,
        };
        if (environmentId) {
            queryParameters.environment_id = environmentId;
        }
        const queryString = new URLSearchParams(queryParameters).toString();
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/manage-recommendations/?${queryString}`);

        const config = {
            url,
            method: 'delete',
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        console.log('deleteRecommendationByIds error', error);
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

export const getUsersDataByQuery = async (token, organizationId, x_API_Key, page, adobe_io_id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/target/users`);
        url.searchParams.append('x_API_Key', x_API_Key);
        url.searchParams.append('page', page);
        if (adobe_io_id) {
            url.searchParams.append('id', adobe_io_id);
        }
        let config = {
            method: 'get',
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const getTasks = async (token, organizationId, role) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions`);
        const headers = getHeaders(token, organizationId)
        let config = {
            method: 'get',
            url: url,
            headers: {
                ...headers,
                role: role
            }
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const getManageAdobeTargetModalApi = async (token, organizationId, role, tenant, campaignId) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/target/manage-adobe-target-model-api`);
        url.searchParams.append('campaignId', campaignId);
        url.searchParams.append('tenant', tenant);
        let config = {
            method: 'get',
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const blockFeaturesForCampaign = async (token, organizationId, tenant, campaignId, blockedFeatures, blockedFeatureSources, isGlobal = false) => {
    try {
        const url = new URL(`${process.env.REACT_APP_NODE_SERVER_BASE_URL}/functions/target/update-blocklist`);
        url.searchParams.append('campaignId', campaignId);
        url.searchParams.append('tenant', tenant);
        url.searchParams.append('isGlobal', isGlobal);
        const config = {
            method: 'put',
            url: url,
            headers: getHeaders(token, organizationId),
            data: {
                blockedFeatures: blockedFeatures,
                blockedFeatureSources: blockedFeatureSources || []
            }
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response?.status) &&
            error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response?.data || error;
    }
};

export const getGlobalBlocklistForCampaign = async (token, organizationId, tenant) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/target/global-blocklist`);
        url.searchParams.append('tenant', tenant);
        const config = {
            method: 'get',
            url: url,
            headers: getHeaders(token, organizationId)
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response?.data || error;
    }
};

export const blockOrUnblockGlobalFeatures = async (token, organizationId, tenant, blockedFeatures, blockedFeatureSources) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/target/update-global-blocklist`);
        url.searchParams.append('tenant', tenant);
        const config = {
            method: 'put',
            url: url,
            headers: getHeaders(token, organizationId),
            data: {
                blockedFeatures: blockedFeatures,
                blockedFeatureSources: blockedFeatureSources || []
            }
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response?.data || error;
    }
};

export const getEntityDataByQuery = async (token, organizationId, activityName, id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/target/entity`);
        url.searchParams.append('tenant', activityName);
        url.searchParams.append('id', id);
        let config = {
            method: 'get',
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const saveEntityData = async (token, organizationId, activityName, data) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/target/entity`);
        url.searchParams.append('tenant', activityName);

        let config = {
            method: 'post',
            url: url,
            headers: getHeaders(token, organizationId),
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const deleteEntityData = async (token, organizationId, activityName, data) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/target/entity`);
        url.searchParams.append('tenant', activityName);
        url.searchParams.append('id', data?.id);
        url.searchParams.append('environment', data?.environment);

        let config = {
            method: 'delete',
            url: url,
            headers: getHeaders(token, organizationId),
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const createExperience = async (token, organizationId, data, eventData = null) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/experiences`);
        let config = {
            method: 'post',
            url: url,
            headers: getHeaders(token, organizationId, "post", 'application/json', eventData),
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const RevertExperienceVersion = async (token, organizationId, experienceId) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/experiences/${experienceId}/revert`);
        let config = {
            method: 'post',
            url: url,
            headers: getHeaders(token, organizationId, "post")
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const getExperienceById = async (token, organizationId, id, adobe_io_id, contentful_id, tealium_id, github_integration_id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/experiences/${id}`);
        if (adobe_io_id) url.searchParams.append('adobe_io_id', adobe_io_id);
        if (contentful_id) url.searchParams.append('contentful_id', contentful_id);
        if (tealium_id) url.searchParams.append('tealium_id', tealium_id);
        if (github_integration_id) url.searchParams.append('github_integration_id', github_integration_id);
        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const getAEMContentFragments = async ({ token, organizationId, name, template, aem_adobe_io_id, aem_folder_url }) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/experiences/aem-content-fragments`);
        if (name) url.searchParams.append("name", name);
        if (template) url.searchParams.append("template", template);
        if (aem_adobe_io_id) url.searchParams.append("aem_adobe_io_id", aem_adobe_io_id);
        if (aem_folder_url) url.searchParams.append("aem_folder_url", aem_folder_url);

        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const getAEMFragmentJsonPath = async (token, organizationId, fragmentId) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/aem-adobe-io/aem-json-path/${fragmentId}`);

        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const createAEMFragmentJsonPath = async (token, organizationId, payload) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/aem-adobe-io/aem-json-path`);

        let config = {
            method: "post",
            url: url,
            headers: getHeaders(token, organizationId),
            data: payload
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const updateAEMFragmentJsonPath = async (token, organizationId, fragmentId, payload) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/aem-adobe-io/aem-json-path/${fragmentId}`);

        let config = {
            method: "put",
            url: url,
            headers: getHeaders(token, organizationId),
            data: payload
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const updateAEMContentFragments = async (token, organizationId, payload, aem_folder_url) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/experiences/aem-content-fragments`);
        if (aem_folder_url) url.searchParams.append("aem_folder_url", aem_folder_url);
        let config = {
            method: "put",
            url: url,
            headers: getHeaders(token, organizationId),
            data: payload
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const getContentfulEntries = async ({ token, organizationId, contentful_id, id }) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/experiences/contentful-entries`);
        if (id) url.searchParams.append("id", id);
        if (contentful_id) url.searchParams.append("contentful_id", contentful_id);

        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const getEntryByPath = async ({ token, organizationId, github_integration_id, path }) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/github-integration/entry`);
        url.searchParams.append("path", path);
        if (github_integration_id) url.searchParams.append("github_integration_id", github_integration_id);

        let config = {
            method: "get",
            url: url.toString(),
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const updateEntryByPath = async ({ token, organizationId, data }) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/github-integration/entry`);

        let config = {
            method: "put",
            url: url.toString(),
            headers: getHeaders(token, organizationId),
            data,
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const createEntryByPath = async ({ token, organizationId, data }) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/github-integration/entry`);

        let config = {
            method: "post",
            url: url.toString(),
            headers: getHeaders(token, organizationId),
            data,
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const getAllGithubBranches = async ({ token, organizationId, data }) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/github-integration/branches`);

        let config = {
            method: "post",
            url: url.toString(),
            headers: getHeaders(token, organizationId),
            data: encryptPayload(data, token),
        };

        const result = await axios.request(config);
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const getGithubEntries = async ({ token, organizationId, github_integration_id, id }) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/github-integration/entries`);
        if (id) url.searchParams.append("id", id);
        if (github_integration_id) url.searchParams.append("github_integration_id", github_integration_id);

        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const getContentTypes = async ({ token, organizationId, contentful_id }) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/contentful-integration/content-types`);
        if (contentful_id) url.searchParams.append("contentful_id", contentful_id);

        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const createContentfulEntry = async ({ token, content_type, experience_id, organizationId, fieldsData, contentful_id }) => {
    try {
        if (!token || !contentful_id) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/contentful-integration/entries`);

        let config = {
            method: "post",
            url: url,
            headers: getHeaders(token, organizationId, "post"),
            data: {
                fieldsData,
                contentful_id,
                content_type,
                experience_id
            },
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        console.log(error)
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const updateContentfulEntry = async ({ token, content_type, organizationId, entry_id, fieldsData, version, contentful_id }) => {
    try {
        if (!token || !entry_id || !version) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/contentful-integration/entries/${entry_id}`);

        let config = {
            method: "patch",
            url: url,
            headers: getHeaders(token, organizationId, "patch"),
            data: {
                fieldsData,
                contentful_id,
                version,
                content_type
            },
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        console.log(error)
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const getContentfulEntriesforBanner = async ({ token, organizationId, space_id, environment_id, access_token }) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/contentful-integration/entries`);
        if (space_id) url.searchParams.append("space_id", space_id);
        if (environment_id) url.searchParams.append("environment_id", environment_id);
        if (access_token) url.searchParams.append("access_token", access_token);

        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const getAllExperiencesData = async (token, organizationId, adobe_io_id, contentful_id, tealium_id, github_integration_id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/experiences`);
        if (adobe_io_id) url.searchParams.append("adobe_io_id", adobe_io_id);
        if (contentful_id) url.searchParams.append("contentful_id", contentful_id);
        if (tealium_id) url.searchParams.append("tealium_id", tealium_id);
        if (github_integration_id) url.searchParams.append("github_integration_id", github_integration_id);
        let config = {
            method: 'get',
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const getExperienceOfferMappingData = async (token, organizationId, experienceId, integrationId, offers = []) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/experiences/offer-mapping`);
        if (experienceId) {
            url.searchParams.append("experience_id", experienceId);
        }
        if (integrationId) {
            url.searchParams.append("integration_id", integrationId);
        }
        if (offers.length > 0) {
            url.searchParams.append("offers", offers.join(","));
        }

        let config = {
            method: 'get',
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const deleteExperienceById = async (token, organizationId, { id, ids, version }, checkDependencies = false, eventData = null) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/experiences${checkDependencies ? "?checkDependencies=true" : ""}`);
        const payload = {};
        if (id) payload.id = id;
        if (ids && ids.length) payload.ids = ids;
        if (version) payload.version = version;
        let config = {
            method: "delete",
            url: url,
            headers: getHeaders(token, organizationId, "delete", 'application/json', eventData),
            data: payload
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const getTenantActivityOffers = async (token, organizationId, { tenant, ids, type }) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/adobe-target-offer/`);
        url.searchParams.append('tenant', tenant);
        url.searchParams.append('type', type);
        if (ids) {
            url.searchParams.append('ids', ids);
        }

        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const createTenantActivityOffers = async (token, organizationId, { tenant, data, experience_id }, eventData = null) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/adobe-target-offer/`);
        let config = {
            method: "post",
            url: url,
            headers: getHeaders(token, organizationId, "post", 'application/json', eventData),
            data: { ...data, tenant, experience_id }
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const updateTenantActivityOffers = async (token, organizationId, data) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/adobe-target-offer/`);
        let config = {
            method: "patch",
            url: url,
            headers: getHeaders(token, organizationId, "patch"),
            data
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const updateAdobeTargetAudience = async (token, organizationId, id, data) => {

    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/target/audience/${id}`);
        let config = {
            method: "post",
            url: url,
            headers: getHeaders(token, organizationId, "post"),
            data: { ...data }
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const getAdobeTargetAudience = async (token, organizationId, { tenant, id }) => {

    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/target/audience`);
        url.searchParams.append('tenant', tenant);
        url.searchParams.append('id', id);
        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const editExperience = async (token, organizationId, id, adobe_io_id, contentful_id, tealium_id, data, eventData = null) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/experiences/${id}`);
        if (adobe_io_id) url.searchParams.append("adobe_io_id", adobe_io_id)
        if (contentful_id) url.searchParams.append("contentful_id", contentful_id)
        if (tealium_id) url.searchParams.append("tealium_id", tealium_id);
        let config = {
            method: 'patch',
            url: url,
            headers: getHeaders(token, organizationId, "patch", 'application/json', eventData),
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const editExperienceOfferMapping = async (token, organizationId, data) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/experiences/offer-mapping`);
        let config = {
            method: 'post',
            url: url,
            headers: getHeaders(token, organizationId, "post"),
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const saveTemplates = async (token, organizationId, data, scope, eventData = null) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/templates`);
        url.searchParams.append('scope', scope);
        let config = {
            method: 'post',
            url: url,
            headers: getHeaders(token, organizationId, "post", 'application/json', eventData),
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const editTemplates = async (token, organizationId, id, data, scope, eventData = null) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/templates/${id}`);
        url.searchParams.append('scope', scope);
        let config = {
            method: 'patch',
            url: url,
            headers: getHeaders(token, organizationId, "patch", 'application/json', eventData),
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const importTemplateFromFigma = async (token, organizationId, data) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/templates/import/figma`);
        let config = {
            method: 'post',
            url: url,
            headers: getHeaders(token, organizationId, "post"),
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const getAllTemplateData = async (token, organizationId, data = {}) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/templates`);
        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                url.searchParams.append(key, data[key]);
            }
        }
        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const deleteTemplateById = async (token, organizationId, id, scope = '', checkDependencies, eventData = null) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/templates/${id}`);
        url.searchParams.append('scope', scope);
        url.searchParams.append('checkDependencies', checkDependencies);
        let config = {
            method: "delete",
            url: url,
            headers: getHeaders(token, organizationId, 'get', 'application/json', eventData)
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const getTemplateById = async (token, organizationId, id, aem_adobe_io_id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/templates/${id}`);
        if (aem_adobe_io_id) url.searchParams.append('aem_adobe_io_id', aem_adobe_io_id);

        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const saveTask = async (token, organizationId, data) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tasks/save-task`);
        let config = {
            method: "post",
            url: url,
            headers: getHeaders(token, organizationId),
            data
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const updateTask = async (token, organizationId, { id, data }) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tasks/save-task/${id}`);
        let config = {
            method: "patch",
            url: url,
            headers: getHeaders(token, organizationId),
            data
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const deleteTask = async (token, organizationId, id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tasks/save-task/${id}`);
        let config = {
            method: "delete",
            url: url,
            headers: getHeaders(token, organizationId),
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const getSavedTasks = async (token, organizationId, id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tasks/saved-tasks`);

        if (id) url.searchParams.append('id', id);
        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId),
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const runTealiumDataLayerCrawler = async (token, organizationId, saved_task_id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tasks/tealium-data-layer-repository`);
        let config = {
            method: "post",
            url: url,
            headers: getHeaders(token, organizationId),
            data: { saved_task_id },
            timeout: 0, // crawl can take a while
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response.data;
    }
};

export const getTealiumDataLayerReportByTaskId = async (token, organizationId, task_id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tealium-data-layer/by-task/${task_id}`);
        const config = {
            method: "get",
            url,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response.data;
    }
};

export const getTealiumDataLayerReport = async (token, organizationId, id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tealium-data-layer`);

        if (id) url.searchParams.append('id', id);
        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId),
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const updateTealiumDataLayer = async (token, organizationId, id, data) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tealium-data-layer/${id}`);
        let config = {
            method: "patch",
            url: url,
            headers: getHeaders(token, organizationId, "patch"),
            data
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const validateTealiumDataLayer = async (token, organizationId, task_id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tealium-data-layer/validate`);
        let config = {
            method: "post",
            url: url,
            headers: getHeaders(token, organizationId, "post"),
            data: { task_id },
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

// NEW: fetch validation-change diffs for all datalayer entries matching the task's domain
export const getTealiumDataLayerValidationChanges = async (token, organizationId, task_id, date_a, date_b) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tealium-data-layer/validation-changes`);
        let config = {
            method: "post",
            url: url,
            headers: getHeaders(token, organizationId, "post"),
            data: { task_id, ...(date_a && date_b ? { date_a, date_b } : {}) },
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const getCrawlHistory = async (token, organizationId, task_id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tasks/crawl-history/${task_id}`);
        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};

export const getUserJourneys = async (token, organizationId) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/user-journeys`);
        const result = await axios.request({ method: "get", url, headers: getHeaders(token, organizationId) });
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response.data;
    }
};

export const getUserJourneyById = async (token, organizationId, journey_id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/user-journeys/${journey_id}`);
        const result = await axios.request({ method: "get", url, headers: getHeaders(token, organizationId) });
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response.data;
    }
};

export const createUserJourney = async (token, organizationId, journey) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tasks/user-journeys`);
        const result = await axios.request({ method: "post", url, headers: getHeaders(token, organizationId, "post"), data: journey });
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response.data;
    }
};

export const updateUserJourney = async (token, organizationId, journey_id, journey) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tasks/user-journeys/${journey_id}`);
        const result = await axios.request({ method: "put", url, headers: getHeaders(token, organizationId, "put"), data: journey });
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response.data;
    }
};

export const deleteUserJourney = async (token, organizationId, journey_id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tasks/user-journeys/${journey_id}`);
        const result = await axios.request({ method: "delete", url, headers: getHeaders(token, organizationId, "delete") });
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response.data;
    }
};

export const linkJourneysToTask = async (token, organizationId, task_id, journey_ids) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tasks/journey-ids/${task_id}`);
        const result = await axios.request({ method: "put", url, headers: getHeaders(token, organizationId, "put"), data: { journey_ids } });
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response.data;
    }
};

export const getSavedTaskLogs = async (token, organizationId, id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tasks/task-logs/${id}`);

        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId),
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
};


// aem adobe io api
export const addNewAEMAdobeIO = async (token, { organizationId, data },) => {
    try {
        if (!token) return;
        let config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/aem-adobe-io`,
            headers: getHeaders(token, organizationId, "post"),
            data: encryptPayload(data, token)
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('addNewTealiumProduct error', error);
        throw error.response.data;
    }
}

export const getAllAEMAdobeIO = async (token, organizationId, id) => {
    try {
        if (!token) return;

        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/aem-adobe-io`);
        if (id) url.searchParams.append('id', id);

        let config = {
            method: 'get',
            url: url,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const getAllAEMAdobeIOFolders = async (token, organizationId, id) => {
    try {
        if (!token) return;

        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/aem-adobe-io/${id}/folders`);

        let config = {
            method: 'get',
            url: url,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const updateSpecificAEMAdobeIO = async (token, organizationId, { id, bodyData }) => {
    try {
        if (!token) return;
        let config = {
            method: 'put',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/aem-adobe-io/${id}`,
            headers: getHeaders(token, organizationId, "put"),
            data: encryptPayload(bodyData, token)
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

export const deleteSpecificAEMAdobeIO = async (token, organizationId, id, checkDependencies = false) => {
    try {
        if (!token) return;
        let config = {
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/aem-adobe-io/${id}${checkDependencies ? "?checkDependencies=true" : ""}`,
            headers: getHeaders(token, organizationId, "delete"),
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

// tealium api methods

export const addNewTealiumProduct = async (token, { organizationId, data },) => {
    try {
        if (!token) return;
        let config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/tealium-key-integration`,
            headers: getHeaders(token, organizationId, "post"),
            data: encryptPayload(data, token)
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('addNewTealiumProduct error', error);
        throw error.response.data;
    }
}

export const getAllTealiumProduct = async (token, organizationId) => {
    try {
        if (!token) return;
        let config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/tealium-key-integration`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const getSpecificTealiumProduct = async (token, organizationId, productId) => {
    try {
        if (!token) return;
        let config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/tealium-key-integration/${productId}`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const updateSpecificTealiumProduct = async (token, organizationId, { productId, bodyData }) => {
    try {
        if (!token) return;
        let config = {
            method: 'put',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/tealium-key-integration/${productId}`,
            headers: getHeaders(token, organizationId),
            data: encryptPayload(bodyData, token)
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

export const deleteSpecificTealiumProduct = async (token, organizationId, productId, checkDependencies = false) => {
    try {
        if (!token) return;
        let config = {
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/tealium-key-integration/${productId}${checkDependencies ? "?checkDependencies=true" : ""}`,
            headers: getHeaders(token, organizationId, "delete"),
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

export const getTealiumAudienceStream = async (token, organizationId, selectedAdobeIoIntegrations) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tealium-audience-stream`);
        url.searchParams.append('adobe_io_integration', selectedAdobeIoIntegrations);

        let config = {
            method: 'get',
            url,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const addTealiumAudienceStream = async (token, organizationId, data) => {
    try {
        if (!token) return;
        let config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/tealium-audience-stream/create`,
            headers: getHeaders(token, organizationId, "post"),
            data
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const getAllAtAudienceConfigRule = async (token, organizationId) => {
    try {
        if (!token) return;
        let config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/at-audience-config-rule`,
            headers: getHeaders(token, organizationId)
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const addAtAudienceConfigRule = async (token, organizationId, data) => {
    try {
        if (!token) return;
        let config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/at-audience-config-rule/create`,
            headers: getHeaders(token, organizationId, "post"),
            data
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}


export const activateTealiumExperience = async (token, { organizationId, data },) => {
    try {
        if (!token) return;
        let config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/tealium-key-integration/activate`,
            headers: getHeaders(token, organizationId, "post"),
            data: data
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('addNewTealiumProduct error', error);
        throw error.response.data;
    }
}

export const getTealiumExperiences = async ({ token, organizationId, tealium_id, id }) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tealium-key-integration/experiences`);
        if (tealium_id) url.searchParams.append('tealium_id', tealium_id);
        if (id) url.searchParams.append('id', id);
        if (!token) return;
        let config = {
            method: 'get',
            url: url,
            headers: getHeaders(token, organizationId, "get"),
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('getTealiumExperiences error', error);
        throw error.response.data;
    }
}

export const updateTealiumExperience = async ({ token, organizationId, data, id },) => {
    try {
        if (!token) return;
        let config = {
            method: 'put',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/tealium-key-integration/experiences/${id}`,
            headers: getHeaders(token, organizationId, "put"),
            data: data
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('addNewTealiumProduct error', error);
        throw error.response.data;
    }
}

export const updateAtAudienceConfigRule = async (token, organizationId, { data, at_audience_config_rule_id }) => {
    try {
        if (!token) return;
        let config = {
            method: 'put',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/at-audience-config-rule/update/${at_audience_config_rule_id}`,
            headers: getHeaders(token, organizationId, "put"),
            data
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

export const getGlobalFunctions = async (token, organizationId) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/admin/functions`);
        let config = {
            method: 'get',
            url: url,
            headers: getHeaders(token, organizationId, "get")
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const getPermissions = async (token, organizationId) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/admin/permissions`);
        let config = {
            method: 'get',
            url: url,
            headers: getHeaders(token, organizationId, "get")
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const updatePermission = async (token, organizationId, id, payload) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/admin/permissions/${id}`);
        let config = {
            method: 'post',
            url: url,
            headers: getHeaders(token, organizationId, "post"),
            data: payload
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const getSetupConfigs = async (token, organizationId) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/organization/setup-configs`);
        let config = {
            method: 'get',
            url: url,
            headers: getHeaders(token, organizationId, "get"),
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const updateSetupConfig = async (token, organizationId, data) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/organization/setup-config`);
        let config = {
            method: 'put',
            url: url,
            headers: getHeaders(token, organizationId, "put"),
            data: data,
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const getOrgLevels = async (token, organization) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/admin/org-levels`);
        let config = {
            method: 'get',
            url: url,
            headers: {
                'Authorization': `Bearer ${token}`,
                "Organization": organization
            }
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const updateOrgLevels = async (token, organization, id, data) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/admin/org-levels/${id}`);
        let config = {
            method: 'put',
            url: url,
            headers: {
                'Authorization': `Bearer ${token}`,
                "Organization": organization
            },
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const createOrgLevel = async (token, organization, data) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/admin/org-levels`);
        let config = {
            method: 'post',
            url: url,
            headers: {
                'Authorization': `Bearer ${token}`,
                "Organization": organization
            },
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const getProducts = async (token) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/admin/products`);
        let config = {
            method: 'get',
            url: url,
            headers: {
                'Authorization': `Bearer ${token}`
            }
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}
export const createProduct = async (token, data) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/admin/products`);
        let config = {
            method: 'post',
            url: url,
            headers: {
                'Authorization': `Bearer ${token}`
            },
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const updateProduct = async (token, id, data) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/admin/products/${id}`);
        let config = {
            method: 'patch',
            url: url,
            headers: {
                'Authorization': `Bearer ${token}`
            },
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const deleteProduct = async (token, id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/admin/products/${id}`);
        let config = {
            method: 'delete',
            url: url,
            headers: {
                'Authorization': `Bearer ${token}`
            }
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const updateTemplateLevels = async (token, id, data) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/templates/${id}/levels`);
        let config = {
            method: 'put',
            url: url,
            headers: {
                'Authorization': `Bearer ${token}`
            },
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const getAllUsers = async (token, organizationId) => {
    try {
        if (!token) return;
        let config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/user/all-users`,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const getAllOrganizations = async (token, org_token) => {
    try {
        if (!token) return;
        let config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/organization`,
            headers: getHeaders(token, org_token)
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const createOrganization = async (token, data) => {
    if (!token) return;
    try {
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/organization/create`,
            headers: getHeaders(token, null, 'post'),
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const editOrganization = async (token, organization_id, data) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/organization/update/${organization_id}`,
            headers: getHeaders(token, organization_id, "post"),
            data: data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('edit organization error', error);
        throw error;
    }
}

export const manualTopup = async (token, organizationId, data) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/plan/org-usage/manual-topup`,
            headers: getHeaders(token, organizationId, 'post'),
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const deleteOrganization = async (token, organization_token) => {
    try {
        if (!token) return;
        const config = {
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/organization/${organization_token}`,
            headers: getHeaders(token, organization_token, "delete"),
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const createNotification = async (token, organizationId, data) => {
    if (!token) return;
    try {
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/notification`,
            headers: getHeaders(token, organizationId, 'post'),
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const deleteNotification = async (token, notification_id, organizationId, type) => {
    try {
        if (!token) return;
        const config = {
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/notification/${notification_id}`,
            headers: getHeaders(token, organizationId, "delete"),
            data: { type }
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const addContentfulIntegration = async (token, { organizationId, data }) => {
    try {
        if (!token) return;
        let config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/contentful-integration`,
            headers: getHeaders(token, organizationId, "post"),
            data: encryptPayload(data, token)
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('add contentful integration error', error);
        throw error.response.data;
    }
}

export const validateContentfulTokens = async (token, organizationId, bodyData) => {
    try {
        if (!token) return;
        let config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/contentful-integration/validate`,
            headers: getHeaders(token, organizationId, "post"),
            data: encryptPayload(bodyData, token)
        };
        const result = await axios.request(config)
        return { ...result.data, data: result.data.data };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

/**
 * Upload an image file as a Contentful asset.
 * Returns the CDN URL string on success.
 */
export const uploadAssetToContentfulAPI = async (token, organizationId, contentfulId, file) => {
    try {
        if (!token || !contentfulId || !file) return null;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('contentful_id', contentfulId);

        const result = await axios.request({
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/contentful-integration/upload-asset?contentful_id=${contentfulId}`,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Organization': organizationId,
                // Do NOT set Content-Type; axios will set multipart boundary automatically
            },
            data: formData
        });
        return result.data?.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

export const getContentfulAsset = async (token, organizationId, assetId, contentfulId) => {
    try {
        if (!token || !assetId || !contentfulId) return null;
        const result = await axios.request({
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/contentful-integration/assets/${assetId}?contentful_id=${contentfulId}`,
            headers: getHeaders(token, organizationId),
        });
        return result.data?.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

export const addGithubIntegration = async (token, { organizationId, data }) => {
    try {
        if (!token) return;
        let config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/github-integration`,
            headers: getHeaders(token, organizationId, "post"),
            data: encryptPayload(data, token)
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('add github integration error', error);
        throw error.response.data;
    }
}

export const getAllContentfulIntegration = async (token, organizationId) => {
    try {
        if (!token) return;
        let config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/contentful-integration`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const getAllGithubIntegration = async (token, organizationId) => {
    try {
        if (!token) return;
        let config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/github-integration`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const getSpecificContentfulIntegration = async (token, organizationId, integrationId) => {
    try {
        if (!token) return;
        let config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/contentful-integration/${integrationId}`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
    }
}

export const getSpecificGithubIntegration = async (token, organizationId, integrationId) => {
    try {
        if (!token) return;
        let config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/github-integration/${integrationId}`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
    }
}

export const getOfferExperienceMappings = async (token, organizationId, offerId, integrationId) => {
    try {
        if (!token) return;

        const queryParameters = { id: offerId }; // required

        if (integrationId) {
            queryParameters.integration_id = integrationId;
        }

        const queryString = new URLSearchParams(queryParameters).toString();
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/experiences/offer-experience-mapping?${queryString}`);

        const config = {
            method: 'get',
            url: url.toString(),
            headers: getHeaders(token, organizationId),
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (
            unauthorizedStatusCode.includes(error?.response?.status) &&
            error?.response?.data?.message?.includes(`timestamp check failed`)
        ) {
            window.localStorage.clear();
            window.location.reload();
        }
        console.error("Failed to fetch offer experience mappings:", error?.response?.data?.message || error);
        return [];
    }
};

export const delinkOfferFromExperience = async (token, organizationId, payload) => {
    try {
        if (!token) return;

        const url = `${REACT_APP_NODE_SERVER_BASE_URL}/experiences/offer-mapping/delink`;

        const config = {
            method: 'post',
            url,
            headers: getHeaders(token, organizationId),
            data: payload, // { experience_id, id, integration_id, destination }
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (
            unauthorizedStatusCode.includes(error?.response?.status) &&
            error?.response?.data?.message?.includes(`timestamp check failed`)
        ) {
            window.localStorage.clear();
            window.location.reload();
        }
        console.error("Failed to delink offer from experience:", error?.response?.data?.message || error);
        return null;
    }
};

export const updateSpecificContentfulIntegration = async (token, organizationId, { integrationId, bodyData }) => {
    try {
        if (!token) return;
        let config = {
            method: 'put',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/contentful-integration/${integrationId}`,
            headers: getHeaders(token, organizationId, "put"),
            data: encryptPayload(bodyData, token)
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

export const updateSpecificGithubIntegration = async (token, organizationId, { integrationId, bodyData }) => {
    try {
        if (!token) return;
        let config = {
            method: 'put',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/github-integration/${integrationId}`,
            headers: getHeaders(token, organizationId, "put"),
            data: encryptPayload(bodyData, token)
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

export const validateGithubIntegrationPathAndToken = async (token, organizationId, bodyData) => {
    try {
        if (!token) return;
        let config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/github-integration/validatePaths`,
            headers: getHeaders(token, organizationId, "post"),
            data: encryptPayload(bodyData, token)
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

export const deleteSpecificContentfulIntegration = async (token, organizationId, integrationId, checkDependencies = false) => {
    try {
        if (!token) return;
        let config = {
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/contentful-integration/${integrationId}${checkDependencies ? "?checkDependencies=true" : ""}`,
            headers: getHeaders(token, organizationId, "delete"),
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

export const deleteSpecificGithubIntegration = async (token, organizationId, integrationId, checkDependencies = false) => {
    try {
        if (!token) return;
        let config = {
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/github-integration/${integrationId}${checkDependencies ? "?checkDependencies=true" : ""}`,
            headers: getHeaders(token, organizationId, "delete"),
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error?.response?.data;
    }
}

export const getAllContentfulData = async () => {
    try {
        let config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/contentful-integration/all-data`,
            headers: {},
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const getNotification = async (token, organizationId) => {
    try {
        let config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/notification/${(token && organizationId) ? "all" : ""}`,
            headers: (token && organizationId) ? getHeaders(token, organizationId) : {},
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const updateNotification = async (token, organizationId, id, data) => {
    try {
        if (!token) return;
        let config = {
            method: 'put',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/notification/${id}`,
            headers: getHeaders(token, organizationId),
            data
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const getAllReleases = async (token, organizationId) => {
    try {
        let config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/release`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const createRelease = async (token, data, organizationId) => {
    if (!token) return;
    try {
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/release/create`,
            headers: getHeaders(token, organizationId, 'post'),
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const updateRelease = async (token, release_id, data, organizationId) => {
    try {
        if (!token) return;
        let config = {
            method: 'patch',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/release/${release_id}`,
            headers: getHeaders(token, organizationId, 'patch'),
            data
        };
        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error;
    }
}

export const deleteRelease = async (token, release_id, organizationId) => {
    try {
        if (!token) return;
        const config = {
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/release/${release_id}`,
            headers: getHeaders(token, organizationId, "delete"),
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}

export const getAllPlans = async (token, organizationId) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/plan`);
        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId)
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (error.response?.status === 401 && error.response?.data?.message?.includes("timestamp check failed")) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response?.data;
    }
};

export const getAllPublicPlans = async () => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/plan/public`);
        let config = {
            method: "get",
            url: url,
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (error.response?.status === 401 && error.response?.data?.message?.includes("timestamp check failed")) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response?.data;
    }
};

export const getPlanById = async (token, organizationId, id) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/plan/${id}`);
        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId)
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (error.response?.status === 401 && error.response?.data?.message?.includes("timestamp check failed")) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response?.data;
    }
};

export const createPlan = async (token, organizationId, data) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/plan`);
        let config = {
            method: "post",
            url: url,
            headers: getHeaders(token, organizationId, "post"),
            data
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (error.response?.status === 401 && error.response?.data?.message?.includes("timestamp check failed")) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response?.data;
    }
};

export const updatePlan = async (token, organizationId, id, data) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/plan/${id}`);
        let config = {
            method: "put",
            url: url,
            headers: getHeaders(token, organizationId, "put"),
            data
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (error.response?.status === 401 && error.response?.data?.message?.includes("timestamp check failed")) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error
    }
};

export const deletePlan = async (token, organizationId, id) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/plan/${id}`);
        let config = {
            method: "delete",
            url: url,
            headers: getHeaders(token, organizationId)
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (error.response?.status === 401 && error.response?.data?.message?.includes("timestamp check failed")) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error
    }
};

export const getOrgPlanMappings = async (token, organizationId, usage) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/plan/org-plan-mappings`);
        if (usage) {
            url.searchParams.append("orgId", organizationId);
            url.searchParams.append("usage", "true");
        }
        let config = {
            method: "get",
            url: url,
            headers: getHeaders(token, organizationId)
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (error.response?.status === 401 && error.response?.data?.message?.includes("timestamp check failed")) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error
    }
};

export const createOrgPlanMapping = async (token, organizationId, data) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/plan/org-plan-mappings`);
        let config = {
            method: "post",
            url: url,
            headers: getHeaders(token, organizationId, "post"),
            data
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (error.response?.status === 401 && error.response?.data?.message?.includes("timestamp check failed")) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error
    }
};

export const updateOrgPlanMapping = async (token, organizationId, id, data) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/plan/org-plan-mappings/${id}`);
        let config = {
            method: "put",
            url: url,
            headers: getHeaders(token, organizationId, "put"),
            data
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (error.response?.status === 401 && error.response?.data?.message?.includes("timestamp check failed")) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error
    }
};

export const deleteOrgPlanMapping = async (token, organizationId, id) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/plan/org-plan-mappings/${id}`);
        let config = {
            method: "delete",
            url: url,
            headers: getHeaders(token, organizationId)
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (error.response?.status === 401 && error.response?.data?.message?.includes("timestamp check failed")) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error
    }
};

export const getFeatureOrgUsage = async (token, organizationId, feature) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/plan/org-usage/${organizationId}/${feature}`);
        let config = {
            method: 'get',
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error.response.data;
    }
}
export const getOrganizationMembers = async (token, organizationId) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/organization/members`);
        url.searchParams.append('organization_id', organizationId);
        let config = {
            method: 'get',
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error
    }
}

export const deleteOrganizationUserInvitaion = async (token, organizationId, invitationId) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/organization/${organizationId}/invitations/${invitationId}`);
        let config = {
            method: 'delete',
            url: url,
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        throw error
    }
}

export const updateUserBlockStatus = async (token, organization_token, user_id, data, eventData = null) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/user/block/${user_id}`);
        let config = {
            method: "post",
            url: url,
            headers: getHeaders(token, organization_token, "post", 'application/json', eventData),
            data
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (error.response?.status === 401 && error.response?.data?.message?.includes("timestamp check failed")) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error
    }
};

export const getDataFromCsvURL = async (url) => {
    try {
        if (!url) return;
        const configUrl = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/csv-url`);
        configUrl.searchParams.append('url', url);
        let config = {
            url: configUrl,
            method: 'get',
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const getEntityMappings = async (token, organizationId) => {
    try {
        if (!token) return;
        const configUrl = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/entity-mapping`);
        let config = {
            url: configUrl,
            method: 'get',
            headers: getHeaders(token, organizationId)
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const createEntityMappings = async (token, organizationId, data) => {
    try {
        if (!token) return;
        const configUrl = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/functions/entity-mapping`);
        let config = {
            url: configUrl,
            method: 'post',
            headers: getHeaders(token, organizationId, "post"),
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const validateTokenAndWorkspace = async (token, organizationId, tenant, adobeIOData) => {
    try {
        if (!token) return;

        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/adobe-io-integration/validateToken`);
        url.searchParams.append('tenant', tenant);
        let config = {
            method: 'post',
            url,
            headers: getHeaders(token, organizationId, "post"),
            data: encryptPayload(adobeIOData, token)
        };
        const result = await axios.request(config)
        return { ...result.data, data: decryptResponse(result.data.data, token) };
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload()
        }
        console.log('validateTokenAndWorkspace error', error);
        throw error.response.data;
    }
}

export const getJourneysForTask = async (token, organizationId, task_id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tasks/crawler-paths/${task_id}`);
        const config = {
            method: "get",
            url,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response.data;
    }
};

export const getPathCrawlResults = async (token, organizationId, task_id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tasks/path-crawl-results/${task_id}`);
        const config = {
            method: "get",
            url,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response.data;
    }
};

export const runCrawlerPath = async (token, organizationId, saved_task_id, journey_id) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/tasks/run-crawler-path`);
        const config = {
            method: "post",
            url,
            headers: getHeaders(token, organizationId, "post"),
            data: { saved_task_id, journey_id },
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error.response.status) && error?.response?.data?.message?.includes(`timestamp check failed`)) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error.response.data;
    }
};
export const createCheckoutSession = async (token, organizationId, checkoutSessionData) => {
    console.log(checkoutSessionData, "checkoutSessionData");
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/stripe/create-checkout-session`);
        let config = {
            url,
            method: 'post',
            headers: getHeaders(token, organizationId),
            data: checkoutSessionData
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const updateSubscriptionPlan = async (token, organizationId, newPlanId, eventData = null) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/stripe/update-subscription`);
        let config = {
            url,
            method: 'post',
            headers: getHeaders(token, organizationId, 'post', 'application/json', eventData),
            data: { newPlanId }
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const cancelSubscription = async (token, organizationId) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/stripe/cancel-subscription`);
        let config = {
            url,
            method: 'post',
            headers: getHeaders(token, organizationId, 'post'),
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}
export const getStripePortalSession = async (token, organizationId, returnUrl) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/stripe/portal-session`);
        let config = {
            url,
            method: 'post',
            headers: getHeaders(token, organizationId, 'post'),
            data: { returnUrl }
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const getPaymentStatus = async (token, organizationId) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/stripe/payment-status`);
        let config = {
            url,
            method: 'get',
            headers: getHeaders(token, organizationId),
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const toggleAutoRenew = async (token, organizationId, autoRenew) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/stripe/toggle-auto-renew`);
        let config = {
            url,
            method: 'post',
            headers: getHeaders(token, organizationId, 'post'),
            data: { autoRenew }
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}
export const getTransactionHistory = async (token, organizationId) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/stripe/transactions`);
        let config = {
            url,
            method: 'get',
            headers: getHeaders(token, organizationId),
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const createRefund = async (token, organizationId, data) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/stripe/refund`);
        let config = {
            url,
            method: 'post',
            headers: getHeaders(token, organizationId, 'post'),
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const getPaymentMethods = async (token, organizationId) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/stripe/payment-methods`);
        let config = {
            url,
            method: 'get',
            headers: getHeaders(token, organizationId),
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const createSetupSession = async (token, organizationId, data) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/stripe/setup-session`);
        let config = {
            url,
            method: 'post',
            headers: getHeaders(token, organizationId, 'post'),
            data
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const createSetupIntent = async (token, organizationId) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/stripe/setup-intent`);
        let config = {
            url,
            method: 'post',
            headers: getHeaders(token, organizationId, 'post'),
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const getSubscriptionDetails = async (token, organizationId) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/stripe/subscription-details`);
        let config = {
            url,
            method: 'get',
            headers: getHeaders(token, organizationId),
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const attachPaymentMethod = async (token, organizationId, paymentMethodId) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/stripe/attach-payment-method`);
        let config = {
            url,
            method: 'post',
            headers: getHeaders(token, organizationId, 'post'),
            data: { paymentMethodId }
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const setDefaultPaymentMethod = async (token, organizationId, paymentMethodId) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/stripe/set-default-payment-method`);
        let config = {
            url,
            method: 'put',
            headers: getHeaders(token, organizationId, 'put'),
            data: { paymentMethodId }
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const deletePaymentMethod = async (token, organizationId, paymentMethodId) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/stripe/payment-method/${paymentMethodId}`);
        let config = {
            url,
            method: 'delete',
            headers: getHeaders(token, organizationId, 'delete'),
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const verifySession = async (sessionId) => {
    try {
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/stripe/verify-session/${sessionId}`);
        let config = {
            url,
            method: 'get',
            headers: getHeaders(window.localStorage.getItem('token')), // Payment success page might not have user context ready
        };

        const result = await axios.request(config)
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const trackAnalyticsEvent = async (token, organizationId, eventData) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/analytics/track`,
            headers: getHeaders(token, organizationId, 'post', 'application/json', eventData),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        // Silently fail to not disrupt user experience
        console.error("Failed to track event:", error);
    }
}

export const getAnalyticsEventsData = async (token, organizationId, startDate, endDate, allOrgs = false) => {
    try {
        if (!token) return;
        const query = new URLSearchParams();
        if (startDate) query.append('startDate', startDate);
        if (endDate) query.append('endDate', endDate);
        if (allOrgs) query.append('allOrgs', 'true');

        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/analytics?${query.toString()}`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const getProductAnalyticsData = async (token, organizationId, startDate, endDate) => {
    try {
        if (!token) return;
        const query = new URLSearchParams();
        if (startDate) query.append('startDate', startDate);
        if (endDate) query.append('endDate', endDate);

        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/analytics/product?${query.toString()}`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        throw error?.response?.data;
    }
}

export const getCSPConfig = async (token, organizationId) => {
    try {
        if (!token) return;
        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/admin/csp`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (error.response?.status === 401 && error.response?.data?.message?.includes("timestamp check failed")) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error;
    }
}

export const updateCSPConfig = async (token, organizationId, data) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/admin/csp`,
            headers: getHeaders(token, organizationId, 'post'),
            data: data
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (error.response?.status === 401 && error.response?.data?.message?.includes("timestamp check failed")) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error;
    }
}

export const getAdobeTargetActivitiesRevisionsData = async (token, organizationId, startDate, endDate) => {
    try {
        if (!token) return;
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/tasks/manage-activity-revisions`,
            headers: getHeaders(token, organizationId),
            params,
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

/**
 * Sends a single question to the Adobe Target AI assistant.
 *
 * Single-shot: no conversation history is sent, so each question is answered
 * independently by the backend.
 *
 * @param {string} token          - Auth0 JWT from userData.token
 * @param {string} organizationId - Encrypted org token from selectedOrganization.org_token
 * @param {string} message        - The user's question
 * @param {string} adobe_io_id    - _id of the globally selected Adobe IO integration
 * @param {string} conversation_id - Opaque id grouping turns; rotate to reset
 * @param {string} product         - Adobe product id (see PRODUCTS in prompts.js)
 */
export const askAiAssistant = async (token, organizationId, message, adobe_io_id, conversation_id, connector, project_id, tealium_key_id, model, connectors) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/ai-assistant/ask`,
            headers: getHeaders(token, organizationId, 'post'),
            // Both integration ids are sent; the server resolves only the one
            // the selected connector needs.
            data: {
                message,
                adobe_io_id,
                conversation_id,
                connector,
                connectors,
                project_id,
                tealium_key_id,
                model,
            },
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

export const abortAiAssistant = async (token, organizationId, conversation_id) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/ai-assistant/abort`,
            headers: getHeaders(token, organizationId, 'post'),
            data: { conversation_id },
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

/**
 * Streams the AI assistant response via Server-Sent Events (SSE).
 */
export const askAiAssistantStream = async ({
    token,
    organizationId,
    message,
    adobe_io_id,
    conversation_id,
    connector,
    connectors,
    project_id,
    tealium_key_id,
    model,
    signal,
    onDelta,
    onStatus,
    onDone,
    onError,
}) => {
    try {
        if (!token) return;
        const url = `${REACT_APP_NODE_SERVER_BASE_URL}/ai-assistant/ask`;
        const headers = {
            ...getHeaders(token, organizationId, 'post'),
            Accept: 'text/event-stream',
        };
        const body = JSON.stringify({
            message,
            adobe_io_id,
            conversation_id,
            connector,
            connectors,
            project_id,
            tealium_key_id,
            model,
            stream: true,
        });

        const response = await fetch(url, {
            method: 'POST',
            headers,
            body,
            signal,
        });

        if (!response.ok) {
            let errorJson;
            try {
                errorJson = await response.json();
            } catch {}
            const errorMsg = errorJson?.message || errorJson?.error || `HTTP ${response.status} error`;
            const err = new Error(errorMsg);
            err.status = response.status;
            throw err;
        }

        if (!response.body) {
            throw new Error('ReadableStream not supported in this browser.');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const blocks = buffer.split('\n\n');
            buffer = blocks.pop() || '';

            for (const block of blocks) {
                if (!block.trim()) continue;
                let eventType = 'message';
                let dataStr = '';
                for (const line of block.split('\n')) {
                    if (line.startsWith('event:')) {
                        eventType = line.slice(6).trim();
                    } else if (line.startsWith('data:')) {
                        dataStr = line.slice(5).trim();
                    }
                }

                if (eventType === 'delta') {
                    try {
                        const parsed = JSON.parse(dataStr);
                        if (parsed.text) onDelta?.(parsed.text);
                    } catch {}
                } else if (eventType === 'status') {
                    try {
                        const parsed = JSON.parse(dataStr);
                        if (parsed.status) onStatus?.(parsed.status);
                    } catch {}
                } else if (eventType === 'done') {
                    try {
                        const parsed = JSON.parse(dataStr);
                        onDone?.(parsed);
                    } catch {}
                } else if (eventType === 'error') {
                    try {
                        const parsed = JSON.parse(dataStr);
                        const err = new Error(parsed.error || 'Streaming error');
                        err.errorCode = parsed.error_code || 'unknown';
                        onError?.(err);
                    } catch {
                        const err = new Error('Streaming error');
                        err.errorCode = 'unknown';
                        onError?.(err);
                    }
                }
            }
        }
    } catch (error) {
        if (error?.name === 'AbortError' || signal?.aborted) {
            return;
        }
        if (onError) {
            onError(error);
        } else {
            throw error;
        }
    }
};

/**
 * Downloads a file the assistant generated.
 *
 * The route requires Authorization and organization headers, so a plain anchor
 * href can't fetch it — pull the bytes as a blob and hand them to the browser
 * through a temporary object URL instead.
 *
 * @param {string} token          - Auth0 JWT from userData.token
 * @param {string} organizationId - Encrypted org token from selectedOrganization.org_token
 * @param {string} filename       - Filename as returned in the reply's files[]
 */
export const downloadAiAssistantFile = async (token, organizationId, filename) => {
    try {
        if (!token) return;
        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/ai-assistant/files/${encodeURIComponent(filename)}`,
            headers: getHeaders(token, organizationId),
            responseType: 'blob',
        };

        const result = await axios.request(config);

        const objectUrl = window.URL.createObjectURL(new Blob([result.data]));
        const link = document.createElement('a');
        link.href = objectUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);

        return true;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

/**
 * Downloads a project context file uploaded to the library.
 *
 * @param {string} token          - Auth0 JWT from userData.token
 * @param {string} organizationId - Encrypted org token from selectedOrganization.org_token
 * @param {string} filename       - Filename stored on the project
 */
export const downloadAiAssistantContextFile = async (token, organizationId, filename) => {
    try {
        if (!token) return;
        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/ai-assistant/context-files/${encodeURIComponent(filename)}`,
            headers: getHeaders(token, organizationId),
            responseType: 'blob',
        };

        const result = await axios.request(config);

        const cleanName = filename.replace(/^[a-f0-9-]{36}-/i, '') || filename;
        const objectUrl = window.URL.createObjectURL(new Blob([result.data]));
        const link = document.createElement('a');
        link.href = objectUrl;
        link.setAttribute('download', cleanName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);

        return true;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}


/**
 * Clears the AI assistant conversation held server-side.
 *
 * The client also rotates its conversation id when clearing, so the next
 * question starts fresh regardless — this call is what releases the previous
 * transcript instead of leaving it to time out.
 *
 * @param {string} token           - Auth0 JWT from userData.token
 * @param {string} organizationId  - Encrypted org token from selectedOrganization.org_token
 * @param {string} conversation_id - The conversation being cleared
 */
export const clearAiAssistantSession = async (token, organizationId, conversation_id) => {
    try {
        if (!token) return;
        const config = {
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/ai-assistant/session`,
            headers: getHeaders(token, organizationId, 'delete'),
            data: { conversation_id },
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

/**
 * Fetches the library of context files and conversation files for projects.
 *
 * @param {string} token          - Auth0 JWT from userData.token
 * @param {string} organizationId - Encrypted org token from selectedOrganization.org_token
 * @param {string} [projectId]    - Optional project ID to scope the library
 * @param {AbortSignal} [signal]  - Optional AbortSignal
 */
export const getAiAssistantLibrary = async (token, organizationId, projectId, signal) => {
    try {
        if (!token || !organizationId) throw new Error('Select an organisation to load library.');
        const config = {
            params: projectId ? { project_id: projectId } : {},
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/ai-assistant/library`,
            headers: getHeaders(token, organizationId),
            signal,
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

// ---------------------------------------------------------------------------
// Product Feeds (Adobe Target Recommendations CSV feeds)
// ---------------------------------------------------------------------------

/**
 * Shared request helper for the /product-feeds endpoints.
 * Publish and preview answer 422 with validation details in `data`, so the
 * whole response body is thrown for the caller to read.
 */
const productFeedsRequest = async ({ token, organizationId, method = 'get', path = '', data, params, responseType }) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/product-feeds${path}`);
        Object.entries(params || {}).forEach(([key, value]) => {
            if (value !== undefined && value !== null) url.searchParams.append(key, value);
        });
        const config = {
            url,
            method,
            headers: getHeaders(token, organizationId, method),
            data,
            responseType,
        };

        const result = await axios.request(config);
        return responseType ? result : result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}
/**
 * Lists the caller's saved assistant conversations, newest activity first.
 * Scoped server-side to the authenticated author.
 */
export const listAiAssistantConversations = async (token, organizationId, projectId) => {
    try {
        if (!token) return;
        const config = {
            params: projectId ? { project_id: projectId } : {},
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/ai-assistant/conversations`,
            headers: getHeaders(token, organizationId),
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

export const getProductFeedMeta = ({ token, organizationId }) =>
    productFeedsRequest({ token, organizationId, path: '/meta' });

export const getProductFeedById = ({ token, organizationId, feedId }) =>
    productFeedsRequest({ token, organizationId, path: `/${feedId}` });

export const createProductFeed = ({ token, organizationId, data }) =>
    productFeedsRequest({ token, organizationId, method: 'post', data });

export const updateProductFeedDraft = ({ token, organizationId, feedId, data }) =>
    productFeedsRequest({ token, organizationId, method: 'patch', path: `/${feedId}/draft`, data });

export const discardProductFeedDraft = ({ token, organizationId, feedId }) =>
    productFeedsRequest({ token, organizationId, method: 'post', path: `/${feedId}/draft/discard` });

export const publishProductFeedConfig = ({ token, organizationId, feedId }) =>
    productFeedsRequest({ token, organizationId, method: 'post', path: `/${feedId}/publish-config` });

export const testProductFeedSource = ({ token, organizationId, data }) =>
    productFeedsRequest({ token, organizationId, method: 'post', path: '/source-test', data });

export const extractProductFeedSource = ({ token, organizationId, data }) =>
    productFeedsRequest({ token, organizationId, method: 'post', path: '/source-extract', data });

// Starts a preview build in the background; poll getProductFeedPreview for the result.
// productLimit: 50, 200 or 0 (all products).
export const previewProductFeed = ({ token, organizationId, feedId, productLimit = 50 }) =>
    productFeedsRequest({ token, organizationId, method: 'post', path: `/${feedId}/preview`, data: { product_limit: productLimit } });

export const getProductFeedPreview = ({ token, organizationId, feedId }) =>
    productFeedsRequest({ token, organizationId, path: `/${feedId}/preview` });

export const runProductFeed = ({ token, organizationId, feedId }) =>
    productFeedsRequest({ token, organizationId, method: 'post', path: `/${feedId}/run` });

export const getProductFeedRuns = ({ token, organizationId, feedId, limit }) =>
    productFeedsRequest({ token, organizationId, path: `/${feedId}/runs`, params: { limit } });

export const getProductFeedRunById = ({ token, organizationId, feedId, runId }) =>
    productFeedsRequest({ token, organizationId, path: `/${feedId}/runs/${runId}` });

export const rotateProductFeedToken = ({ token, organizationId, feedId }) =>
    productFeedsRequest({ token, organizationId, method: 'post', path: `/${feedId}/rotate-token` });

export const setProductFeedEnabled = ({ token, organizationId, feedId, enabled }) =>
    productFeedsRequest({ token, organizationId, method: 'patch', path: `/${feedId}/enabled`, data: { enabled } });

export const getProductFeedTargetConnections = ({ token, organizationId }) =>
    productFeedsRequest({ token, organizationId, path: '/target/connections' });

export const getProductFeedTargetEnvironments = ({ token, organizationId, integrationId }) =>
    productFeedsRequest({ token, organizationId, path: '/target/environments', params: { integration_id: integrationId } });
/** Full transcript for one saved conversation, used to resume it. */
export const getAiAssistantConversation = async (token, organizationId, conversationId, projectId) => {
    try {
        if (!token) return;
        const config = {
            params: projectId ? { project_id: projectId } : {},
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/ai-assistant/conversations/${encodeURIComponent(conversationId)}`,
            headers: getHeaders(token, organizationId),
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

export const deleteAiAssistantConversation = async (token, organizationId, conversationId, projectId) => {
    try {
        if (!token) return;
        const config = {
            params: projectId ? { project_id: projectId } : {},
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/ai-assistant/conversations/${encodeURIComponent(conversationId)}`,
            headers: getHeaders(token, organizationId, 'delete'),
        };

        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

// ── Experience Personalisation Module (EPM) API helpers ─────────────────────

// tags
export const getAllEpmTags = async (token, organizationId, name = null) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/epm/tags`);
        if (name) url.searchParams.append('name', name);
        const config = {
            method: 'get',
            url: url.toString(),
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

export const getEpmTagById = async (token, organizationId, id) => {
    try {
        if (!token) return;
        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/tags/${id}`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

export const createEpmTag = async (token, organizationId, data) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/tags`,
            headers: getHeaders(token, organizationId, 'post'),
            data
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

export const updateEpmTag = async (token, organizationId, id, data) => {
    try {
        if (!token) return;
        const config = {
            method: 'patch',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/tags/${id}`,
            headers: getHeaders(token, organizationId, 'patch'),
            data
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

export const deleteEpmTag = async (token, organizationId, id) => {
    try {
        if (!token) return;
        const config = {
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/tags/${id}`,
            headers: getHeaders(token, organizationId, 'delete'),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

// learnings
export const getAllEpmLearnings = async (token, organizationId) => {
    try {
        if (!token) return;
        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/learnings`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

export const getEpmLearningById = async (token, organizationId, id) => {
    try {
        if (!token) return;
        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/learnings/${id}`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

export const createEpmLearning = async (token, organizationId, data) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/learnings`,
            headers: getHeaders(token, organizationId, 'post'),
            data
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

export const updateEpmLearning = async (token, organizationId, id, data) => {
    try {
        if (!token) return;
        const config = {
            method: 'patch',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/learnings/${id}`,
            headers: getHeaders(token, organizationId, 'patch'),
            data
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

export const deleteEpmLearning = async (token, organizationId, id) => {
    try {
        if (!token) return;
        const config = {
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/learnings/${id}`,
            headers: getHeaders(token, organizationId, 'delete'),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

// tests
export const getAllEpmTests = async (token, organizationId, { status, tag_id, learning_id, name } = {}) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/epm/tests`);
        if (status) url.searchParams.append('status', status);
        if (tag_id) url.searchParams.append('tag_id', tag_id);
        if (learning_id) url.searchParams.append('learning_id', learning_id);
        if (name) url.searchParams.append('name', name);
        
        const config = {
            method: 'get',
            url: url.toString(),
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

export const getEpmTestById = async (token, organizationId, id) => {
    try {
        if (!token) return;
        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/tests/${id}`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

export const createEpmTest = async (token, organizationId, data) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/tests`,
            headers: getHeaders(token, organizationId, 'post'),
            data
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

export const updateEpmTest = async (token, organizationId, id, data) => {
    try {
        if (!token) return;
        const config = {
            method: 'patch',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/tests/${id}`,
            headers: getHeaders(token, organizationId, 'patch'),
            data
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

export const deleteEpmTest = async (token, organizationId, id) => {
    try {
        if (!token) return;
        const config = {
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/tests/${id}`,
            headers: getHeaders(token, organizationId, 'delete'),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}


// Projects always use the explicitly selected organisation.
export const getProjects = async (token, organizationId, signal) => {
    if (!token || !organizationId) throw new Error('Select an organisation to load projects.');
    const result = await axios.request({
        method: 'get',
        url: `${REACT_APP_NODE_SERVER_BASE_URL}/projects`,
        headers: getHeaders(token, organizationId),
        signal,
    });
    return result.data;
};

export const createProject = async (token, organizationId, data, signal) => {
    if (!token || !organizationId) throw new Error('Select an organisation to create a project.');
    const result = await axios.request({
        method: 'post',
        url: `${REACT_APP_NODE_SERVER_BASE_URL}/projects`,
        headers: getHeaders(token, organizationId, 'post'),
        data,
        signal,
    });
    return result.data;
};

export const getProject = async (token, organizationId, projectId, signal) => {
    if (!token || !organizationId) throw new Error('Select an organisation to load project context.');
    const result = await axios.request({
        method: 'get',
        url: `${REACT_APP_NODE_SERVER_BASE_URL}/projects/${encodeURIComponent(projectId)}`,
        headers: getHeaders(token, organizationId), signal,
    });
    return result.data;
};

export const uploadProjectContext = async (token, organizationId, projectId, file, signal) => {
    if (!token || !organizationId) throw new Error('Select an organisation to upload context.');
    const data = new FormData();
    data.append('file', file);
    const result = await axios.request({
        method: 'post',
        url: `${REACT_APP_NODE_SERVER_BASE_URL}/projects/${encodeURIComponent(projectId)}/context`,
        headers: getHeaders(token, organizationId, 'post', null), data, signal,
    });
    return result.data;
};

export const deleteProjectContext = async (token, organizationId, projectId, filename, signal) => {
    if (!token || !organizationId) throw new Error('Select an organisation to remove context.');
    const result = await axios.request({
        method: 'delete',
        url: `${REACT_APP_NODE_SERVER_BASE_URL}/projects/${encodeURIComponent(projectId)}/context/${encodeURIComponent(filename)}`,
        headers: getHeaders(token, organizationId, 'delete'), signal,
    });
    return result.data;
};

export const deleteProject = async (token, organizationId, projectId, signal) => {
    if (!token || !organizationId) throw new Error('Select an organisation to delete a project.');
    const result = await axios.request({
        method: 'delete',
        url: `${REACT_APP_NODE_SERVER_BASE_URL}/projects/${encodeURIComponent(projectId)}`,
        headers: getHeaders(token, organizationId, 'delete'), signal,
    });
    return result.data;
};
export const uploadEpmTestAttachments = async (token, organizationId, formData) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/tests/upload`,
            headers: getHeaders(token, organizationId, 'post', 'multipart/form-data'),
            data: formData
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
}

// test-locations
export const getAllEpmTestLocations = async (token, organizationId, params = {}) => {
    try {
        if (!token) return;
        const url = new URL(`${REACT_APP_NODE_SERVER_BASE_URL}/epm/test-locations`);
        if (params?.search) url.searchParams.append('search', params.search);
        if (params?.field_type) url.searchParams.append('field_type', params.field_type);
        if (params?.conflict_role) url.searchParams.append('conflict_role', params.conflict_role);
        const config = {
            method: 'get',
            url: url.toString(),
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
};

export const getEpmTestLocationById = async (token, organizationId, id) => {
    try {
        if (!token) return;
        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/test-locations/${id}`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
};

export const createEpmTestLocation = async (token, organizationId, data) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/test-locations`,
            headers: getHeaders(token, organizationId, 'post'),
            data
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
};

export const updateEpmTestLocation = async (token, organizationId, id, data) => {
    try {
        if (!token) return;
        const config = {
            method: 'patch',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/test-locations/${id}`,
            headers: getHeaders(token, organizationId, 'patch'),
            data
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
};

export const deleteEpmTestLocation = async (token, organizationId, id) => {
    try {
        if (!token) return;
        const config = {
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/test-locations/${id}`,
            headers: getHeaders(token, organizationId, 'delete'),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
};

export const reorderEpmTestLocations = async (token, organizationId, data) => {
    try {
        if (!token) return;
        const config = {
            method: 'patch',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/test-locations/reorder`,
            headers: getHeaders(token, organizationId, 'patch'),
            data
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
};

// checklists
export const getAllEpmChecklists = async (token, organizationId, params = {}) => {
    try {
        if (!token) return;
        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/checklists`,
            headers: getHeaders(token, organizationId),
            params
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
};

export const getEpmChecklistById = async (token, organizationId, id) => {
    try {
        if (!token) return;
        const config = {
            method: 'get',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/checklists/${id}`,
            headers: getHeaders(token, organizationId),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
};

export const createEpmChecklist = async (token, organizationId, data) => {
    try {
        if (!token) return;
        const config = {
            method: 'post',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/checklists`,
            headers: getHeaders(token, organizationId, 'post'),
            data
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
};

export const updateEpmChecklist = async (token, organizationId, id, data) => {
    try {
        if (!token) return;
        const config = {
            method: 'patch',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/checklists/${id}`,
            headers: getHeaders(token, organizationId, 'patch'),
            data
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
};

export const deleteEpmChecklist = async (token, organizationId, id) => {
    try {
        if (!token) return;
        const config = {
            method: 'delete',
            url: `${REACT_APP_NODE_SERVER_BASE_URL}/epm/checklists/${id}`,
            headers: getHeaders(token, organizationId, 'delete'),
        };
        const result = await axios.request(config);
        return result.data;
    } catch (error) {
        if (unauthorizedStatusCode.includes(error?.response?.status) && error?.response?.data?.message?.includes('timestamp check failed')) {
            window.localStorage.clear();
            window.location.reload();
        }
        throw error?.response?.data || error;
    }
};

export const updateProject = async (token, organizationId, projectId, data, signal) => {
    if (!token || !organizationId) throw new Error('Select an organisation to update a project.');
    const result = await axios.request({
        method: 'patch',
        url: `${REACT_APP_NODE_SERVER_BASE_URL}/projects/${encodeURIComponent(projectId)}`,
        headers: getHeaders(token, organizationId, 'patch'), data, signal,
    });
    return result.data;
};

export const getAiAssistantBackgroundRun = async (token, organizationId, projectId, conversationId) => {
    const result = await axios.request({
        method: 'get', url: `${REACT_APP_NODE_SERVER_BASE_URL}/ai-assistant/background`,
        headers: getHeaders(token, organizationId),
        params: { ...(projectId ? { project_id: projectId } : {}), ...(conversationId ? { conversation_id: conversationId } : {}) },
    });
    return result.data;
};

// ── Admin OpenRouter Workspace APIs ──────────────────────────────────────────

export const getOpenRouterWorkspaces = async (token, organizationId) => {
    if (!token) return;
    const config = {
        method: 'get',
        url: `${REACT_APP_NODE_SERVER_BASE_URL}/admin/openrouter-workspaces`,
        headers: getHeaders(token, organizationId, 'get'),
    };
    const result = await axios.request(config);
    return result.data;
};

export const getAvailableOpenRouterWorkspaces = async (token, organizationId) => {
    if (!token) return;
    const config = {
        method: 'get',
        url: `${REACT_APP_NODE_SERVER_BASE_URL}/admin/openrouter-workspaces/available`,
        headers: getHeaders(token, organizationId, 'get'),
    };
    const result = await axios.request(config);
    return result.data;
};

export const getOpenRouterWorkspaceByOrgId = async (token, orgId, organizationId) => {
    if (!token || !orgId) return;
    const config = {
        method: 'get',
        url: `${REACT_APP_NODE_SERVER_BASE_URL}/admin/openrouter-workspaces/${encodeURIComponent(orgId)}`,
        headers: getHeaders(token, organizationId, 'get'),
    };
    const result = await axios.request(config);
    return result.data;
};

export const createOpenRouterWorkspace = async (token, data, organizationId) => {
    if (!token) return;
    const config = {
        method: 'post',
        url: `${REACT_APP_NODE_SERVER_BASE_URL}/admin/openrouter-workspaces`,
        headers: getHeaders(token, organizationId, 'post'),
        data,
    };
    const result = await axios.request(config);
    return result.data;
};

export const updateOpenRouterWorkspace = async (token, orgId, data, organizationId) => {
    if (!token || !orgId) return;
    const config = {
        method: 'put',
        url: `${REACT_APP_NODE_SERVER_BASE_URL}/admin/openrouter-workspaces/${encodeURIComponent(orgId)}`,
        headers: getHeaders(token, organizationId, 'put'),
        data,
    };
    const result = await axios.request(config);
    return result.data;
};

export const rotateOpenRouterWorkspaceKey = async (token, orgId, organizationId) => {
    if (!token || !orgId) return;
    const config = {
        method: 'put',
        url: `${REACT_APP_NODE_SERVER_BASE_URL}/admin/openrouter-workspaces/${encodeURIComponent(orgId)}`,
        headers: getHeaders(token, organizationId, 'put'),
        data: { rotate_key: true },
    };
    const result = await axios.request(config);
    return result.data;
};

export const deleteOpenRouterWorkspace = async (token, orgId, organizationId) => {
    if (!token || !orgId) return;
    const config = {
        method: 'delete',
        url: `${REACT_APP_NODE_SERVER_BASE_URL}/admin/openrouter-workspaces/${encodeURIComponent(orgId)}`,
        headers: getHeaders(token, organizationId, 'delete'),
    };
    const result = await axios.request(config);
    return result.data;
};

export const getOpenRouterLogs = async (token, organizationId, params = {}) => {
    if (!token) return;
    const cleanParams = {};
    Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
            cleanParams[key] = params[key];
        }
    });
    const queryString = new URLSearchParams(cleanParams).toString();
    const config = {
        method: 'get',
        url: `${REACT_APP_NODE_SERVER_BASE_URL}/admin/openrouter-workspaces/logs${queryString ? `?${queryString}` : ''}`,
        headers: getHeaders(token, organizationId, 'get'),
    };
    const result = await axios.request(config);
    return result.data;
};

